import { Component, computed, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DropdownItemComponent } from '../../../components/dropdown/dropdown-item.component';
import { InvestmentGoal } from '../../../domain/lti/lti.domain';
import { ValueAccessorProviderFactory } from '../../../components/value-accessor/value-accessor-provider-factory';
import { DropdownToggleDirective } from '../../../components/dropdown/dropdown-toggle.directive';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';

@Component({
  selector: 'td-wb-goal-selector',
  templateUrl: 'goal-selector.component.html',
  styleUrls: ['goal-selector.component.scss'],
  providers: [ValueAccessorProviderFactory(GoalSelectorComponent)],
  imports: [
    DropdownComponent,
    DropdownToggleDirective,
    TranslatePipe,
    FormsModule,
    DropdownItemComponent
  ]
})
export class GoalSelectorComponent implements ControlValueAccessor {
  readonly goals = input<Array<InvestmentGoal> | undefined>(undefined);
  readonly goal = input<InvestmentGoal | undefined>(undefined);
  readonly goalChanged = output<InvestmentGoal>();
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private readonly disabledByOwner = signal(false);
  /** Owner-driven disabled state, forwarded to the inner dropdown. */
  readonly isDisabled = computed((): boolean => this.disabledByOwner());
  private readonly valueState = signal<InvestmentGoal | null | undefined>(undefined);
  private readonly matchedInputGoal = computed((): InvestmentGoal | undefined => {
    const currentGoal = this.goal();
    if (currentGoal === undefined) {
      return undefined;
    }

    const goals = this.goals() ?? [];
    if (currentGoal.id === undefined) {
      return currentGoal;
    }

    return goals.find((goal: InvestmentGoal): boolean => goal.id === currentGoal.id) ?? currentGoal;
  });

  constructor() {
  }

  get value(): InvestmentGoal | null | undefined {
    const currentValue = this.valueState();
    return currentValue === undefined ? this.matchedInputGoal() : currentValue;
  }

  set value(newValue: InvestmentGoal | null | undefined) {
    this.valueState.set(newValue);
  }

  protected readonly selectedGoalName = computed(() => this.value?.name ?? '');

  writeValue(value: InvestmentGoal | null | undefined): void {
    this.valueState.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  onModelChange(event: InvestmentGoal): void {
    this.valueState.set(event);
    this.onChangeCallback(event);
    this.onTouchedCallback();
    this.goalChanged.emit(event);
  }

  comparator(goal1: InvestmentGoal, goal2: InvestmentGoal): boolean {
    return goal1.id === goal2.id;
  }

}

